import { useMemo } from "react";
import { Link } from "react-router-dom";
import {
  Images, ListChecks, PlugsConnected, Palette, Sparkle, PencilSimple,
  ArrowClockwise, CaretRight, CurrencyDollar,
} from "@phosphor-icons/react";
import { PromoPageHeader } from "@/promotions/components/PromoPageHeader";
import { KpiCard } from "@/promotions/components/KpiCard";
import { EmptyState } from "@/promotions/components/EmptyState";
import { StatusBadge } from "@/promotions/components/StatusBadge";
import { CardsSkeleton, KpiSkeleton } from "@/promotions/components/Skeleton";
import { MediaImage } from "../components/MediaImage";
import { useCreativeData, mediaUrl } from "../useCreative";
import { fmtRelative, fmtCost } from "../creativeUtils";

const QUICK_ACTIONS = [
  { key: "generate", to: "/creative/generate", Icon: Sparkle, label: "Generate", ai: true },
  { key: "edit", to: "/creative/edit", Icon: PencilSimple, label: "Edit" },
  { key: "assets", to: "/creative/assets", Icon: Images, label: "Assets" },
  { key: "brand-kits", to: "/creative/brand-kits", Icon: Palette, label: "Brand Kits" },
];

export default function CreativeDashboard() {
  const { jobs, assets, providers, brandKits, mediaMap, analytics, loading, reload } = useCreativeData();

  const m = useMemo(() => {
    const completed = jobs.filter((j) => j.status === "completed");
    const running = jobs.filter((j) => ["queued", "running"].includes(j.status));
    const failed = jobs.filter((j) => ["failed", "dead_letter"].includes(j.status));
    const activeProviders = providers.filter((p) => p.lifecycle === "active");
    return { completed, running, failed, activeProviders };
  }, [jobs, providers]);

  const recentAssets = assets.slice(0, 8);
  const recentJobs = jobs.slice(0, 6);

  return (
    <div className="space-y-8" data-testid="creative-dashboard">
      <PromoPageHeader
        overline="Creative Studio"
        title="Dashboard"
        description="Generate, edit and manage on-brand AI creative for every channel."
        actions={
          <div className="flex items-center gap-2">
            <button
              onClick={reload}
              className="flex items-center gap-1.5 rounded-md border border-border px-3 py-2 text-sm text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
              data-testid="dash-refresh"
            >
              <ArrowClockwise size={14} className={loading ? "animate-spin" : ""} /> Refresh
            </button>
            <Link
              to="/creative/generate"
              className="flex items-center gap-2 rounded-md bg-ai px-4 py-2 text-sm font-medium text-ai-foreground transition-opacity hover:opacity-90"
              data-testid="dash-generate"
            >
              <Sparkle size={16} weight="fill" /> New Generation
            </Link>
          </div>
        }
      />

      {loading ? (
        <KpiSkeleton count={5} />
      ) : (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5" data-testid="kpi-grid">
          <KpiCard index={0} testId="kpi-assets" label="Assets" value={assets.length} Icon={Images} hint={`${m.completed.length} completed jobs`} />
          <KpiCard index={1} testId="kpi-jobs" label="Jobs" value={jobs.length} Icon={ListChecks} hint={`${m.running.length} in progress`} />
          <KpiCard index={2} testId="kpi-failed" label="Failed" value={m.failed.length} Icon={ListChecks} hint={m.failed.length ? "Needs attention" : "All healthy"} />
          <KpiCard index={3} testId="kpi-providers" label="Providers" value={m.activeProviders.length} Icon={PlugsConnected} hint={`${providers.length} registered`} />
          <KpiCard index={4} testId="kpi-spend" label="Spend" value={fmtCost(analytics?.total_cost)} Icon={CurrencyDollar} hint="Settled generation cost" />
        </div>
      )}

      <div className="flex flex-wrap gap-2" data-testid="quick-actions">
        {QUICK_ACTIONS.map(({ key, to, Icon, label, ai }) => (
          <Link
            key={key}
            to={to}
            data-testid={`qa-${key}`}
            className={`flex items-center gap-2 rounded-md border px-3.5 py-2 text-sm font-medium transition-colors ${
              ai ? "border-ai/30 bg-ai/10 text-ai hover:bg-ai/20" : "border-border bg-card hover:bg-accent/40"
            }`}
          >
            <Icon size={16} weight="duotone" /> {label}
          </Link>
        ))}
      </div>

      <section className="space-y-4" data-testid="recent-assets">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-medium tracking-tight">Recent creative</h3>
          <Link to="/creative/assets" className="flex items-center gap-1 text-xs text-muted-foreground transition-colors hover:text-foreground">
            View all <CaretRight size={12} weight="bold" />
          </Link>
        </div>
        {loading ? (
          <CardsSkeleton count={4} />
        ) : recentAssets.length === 0 ? (
          <EmptyState
            Icon={Images}
            title="No creative yet"
            description="Generate your first on-brand asset to see it here."
            action={<Link to="/creative/generate" className="rounded-md bg-ai px-4 py-2 text-sm font-medium text-ai-foreground">Generate</Link>}
          />
        ) : (
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4" data-testid="recent-assets-grid">
            {recentAssets.map((a) => (
              <Link
                key={a.id}
                to="/creative/assets"
                className="group overflow-hidden rounded-lg border border-border bg-card transition-colors hover:border-brand/40"
                data-testid={`recent-asset-${a.id}`}
              >
                <div className="aspect-square w-full overflow-hidden">
                  <MediaImage
                    url={mediaUrl(mediaMap, a.media_asset_id, "thumbnail")}
                    alt={a.operation}
                    className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                  />
                </div>
                <div className="flex items-center justify-between gap-2 px-3 py-2">
                  <span className="truncate text-xs capitalize text-muted-foreground">{a.kind} · {a.operation}</span>
                  <StatusBadge status={a.status} dot={false} />
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>

      <section className="space-y-4" data-testid="recent-jobs">
        <h3 className="text-lg font-medium tracking-tight">Recent jobs</h3>
        {recentJobs.length === 0 ? (
          <EmptyState Icon={ListChecks} title="No jobs yet" description="Generation jobs will appear here." />
        ) : (
          <div className="overflow-hidden rounded-lg border border-border bg-card">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="promo-glass border-b border-border">
                  <tr>
                    {["Job", "Provider", "Status", "Cost", "Created"].map((h) => (
                      <th key={h} className="px-4 py-3 text-left promo-overline whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {recentJobs.map((j) => (
                    <tr key={j.id} className="border-b border-border/60 last:border-0 hover:bg-accent/30" data-testid={`job-row-${j.id}`}>
                      <td className="px-4 py-3 font-mono text-xs">{j.id.slice(0, 8)}</td>
                      <td className="px-4 py-3 text-muted-foreground">{j.provider || "—"}</td>
                      <td className="px-4 py-3"><StatusBadge status={j.status} /></td>
                      <td className="px-4 py-3 tabular-nums">{fmtCost(j.resolved_cost)}</td>
                      <td className="px-4 py-3 tabular-nums text-muted-foreground">{fmtRelative(j.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
